import { DosageTemplate } from '@dreryk/egabinet-prescription-frontend-api';

import { PromiseVoidFunction } from '@dreryk/react-common';

import { FormsTemplates } from '~libs.desktop.shared.user-templates.forms';

import { extendedDosageTemplatesTranslationKeys } from '../../../../translations';

import { DosageTemplateForm, DosageTemplateFormChildrenRenderProps } from './form/DosageTemplateForm';
import { DosageInitialEditTemplate, DosageTemplateListItem } from './DosageTemplates.types';
import { DosageTemplatesList } from './DosageTemplatesList';
import { createDosageTemplateQuery } from './DosageTemplates.utils';
import { DosageTemplatesSendTemplateButton } from './DosageTemplatesSendTemplateButton';
import { DosageTemplateReadOnly } from './readOnly/DosageTemplateReadOnly';

type DosageTemplatesEditSlotRenderProps = DosageTemplateFormChildrenRenderProps & {
   onTemplateSaveSuccess: PromiseVoidFunction;
   ready: boolean;
};

type DosageTemplatesProps = {
   readonly initialEditTemplate?: DosageInitialEditTemplate;
   readonly readOnlySlot?: JSX.Element;
   readonly editSlot: (renderProps: DosageTemplatesEditSlotRenderProps) => JSX.Element;
   readonly children?: JSX.Element;
};

export const DosageTemplates = (props: DosageTemplatesProps): JSX.Element => {
   return (
      <FormsTemplates>
         <FormsTemplates.Search<DosageTemplate>
            queryCreator={createDosageTemplateQuery}
            testId={'dosageTemplatesSearch'}
            placeholder={extendedDosageTemplatesTranslationKeys.searchTemplate}
         >
            {({ templates, templatesExist, loading, refetch }) => (
               <FormsTemplates.State<DosageTemplateListItem>
                  templates={templates}
                  initialEditItem={props.initialEditTemplate}
               >
                  <>
                     {loading ? null : <DosageTemplatesList onTemplateRemoved={refetch} />}

                     <FormsTemplates.ReadOnlySection<DosageTemplate>>
                        {({ activeItem }) => (
                           <>
                              <DosageTemplateReadOnly item={activeItem} />
                              {props.readOnlySlot}
                           </>
                        )}
                     </FormsTemplates.ReadOnlySection>
                     <FormsTemplates.FormSection<DosageTemplateListItem>>
                        {({ activeItem, disableSwitching, enableSwitching, setActiveItemName, templatesApiRef }) => (
                           <DosageTemplateForm
                              template={activeItem}
                              onDirty={disableSwitching}
                              onPristine={enableSwitching}
                              templatesApiRef={templatesApiRef}
                              onTemplateNameChange={setActiveItemName}
                           >
                              {(formSectionProps) =>
                                 props.editSlot({ ...formSectionProps, ready: loading, onTemplateSaveSuccess: refetch })
                              }
                           </DosageTemplateForm>
                        )}
                     </FormsTemplates.FormSection>

                     {props.children}

                     {templatesExist || loading ? null : (
                        <>
                           <FormsTemplates.EmptyStatePlaceholder
                              subheading={extendedDosageTemplatesTranslationKeys.noTemplatesAddedYet}
                           />
                           <FormsTemplates.AddNewItemButton />
                        </>
                     )}
                  </>
               </FormsTemplates.State>
            )}
         </FormsTemplates.Search>
      </FormsTemplates>
   );
};

DosageTemplates.SendTemplateButton = DosageTemplatesSendTemplateButton;
